<template>
  <div>
    <!-- Signature prompt section -->
    <div class="signature-prompt">
      <div class="signature-guide">
        <div class="guide-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
        </div>
        <div class="guide-text">
          <h3>{{ promptTitle }}</h3>
          <p v-html="promptMessage"></p>
        </div>
      </div>
      <button @click="openSignaturePanel" class="sign-btn">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon">
          <path d="M15.5 8H13l-4 10M15.5 8c-.83 0-1.5.67-1.5 1.5 0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5c0-.83-.67-1.5-1.5-1.5z"/>
          <line x1="4" y1="18" x2="20" y2="18"/>
        </svg>
        {{ buttonText }}
      </button>
    </div>

    <!-- Signature panel section (overlay) - only render when showing -->
    <teleport to="body" v-if="showSignaturePanel">
      <div class="signature-panel-overlay">
        <!-- Signature panel -->
        <div class="signature-panel-wrapper">
          <div class="signature-panel">
            <div class="signature-panel-header">
              <button @click="closeSignaturePanel" class="close-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div class="signature-container">
              <canvas ref="signatureCanvas" class="signature-pad"></canvas>
              <div v-if="!isSignatureDrawn" class="signature-instructions">
                <div class="instruction-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15.5 8H13l-4 10M15.5 8c-.83 0-1.5.67-1.5 1.5 0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5c0-.83-.67-1.5-1.5-1.5z"/>
                    <line x1="4" y1="18" x2="20" y2="18"/>
                  </svg>
                </div>
                <p>{{ instructionText }}</p>
              </div>
            </div>
            <div class="signature-panel-actions">
              <button @click="clearSignature" class="secondary-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                {{ clearButtonText }}
              </button>
              <button @click="completeSignature" :disabled="!isSignatureDrawn" class="primary-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="button-icon">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                {{ completeButtonText }}
              </button>
            </div>
            <p class="legal-disclaimer">{{ legalText }}</p>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad';

export default {
  name: 'SignatureComponent',
  props: {
    promptTitle: {
      type: String,
      default: '請簽署本協議'
    },
    promptMessage: {
      type: String,
      default: '請點擊下方「開始簽署」按鈕，在簽名板上簽署你的<strong>完整姓名</strong>，以完成本協議。'
    },
    buttonText: {
      type: String,
      default: '開始簽署'
    },
    instructionText: {
      type: String,
      default: '請在此區域簽署你的完整姓名'
    },
    clearButtonText: {
      type: String,
      default: '清除簽名'
    },
    completeButtonText: {
      type: String,
      default: '完成簽署'
    },
    legalText: {
      type: String,
      default: '提交簽名代表你已閱讀、理解並同意本協議。電子簽名與手寫簽名具相同法律效力。'
    },
    tabletMinWidth: {
      type: Number,
      default: 768
    }
  },
  data() {
    return {
      showSignaturePanel: false,
      signaturePad: null,
      isSignatureDrawn: false,
      isPortraitMode: false,
      isTablet: false,
      signatureData: null,
      rotationLockDetected: false,
      orientationChangeCount: 0,
      orientationCheckTimer: null,
      // Cache for performance
      dpr: window.devicePixelRatio || 1,
      // Using a passive resize handler
      resizeObserver: null
    };
  },
  created() {
    // Create resize observer if supported
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.debounce( ()=> {
        this.checkDeviceAndOrientation();
        if (this.showSignaturePanel && (!this.isPortraitMode || this.isTablet)) {
          this.initSignaturePad();
        }
      }, 200));
    }
  },
  methods: {
    // Debounce utility for performance optimization
    debounce(fn, delay) {
      let timer = null;
      return function(...args) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
          timer = null;
        }, delay);
      };
    },

    openSignaturePanel() {
      // Lock body scrolling
      document.body.style.overflow = 'hidden';

      // Check device type and orientation
      this.checkDeviceAndOrientation();
      this.showSignaturePanel = true;

      // Initialize signature pad in both portrait and landscape
      this.$nextTick(() => {
        // Use requestAnimationFrame for smoother rendering
        requestAnimationFrame(() => {
          this.initSignaturePad();
        });

        // Efficient event handling
        if (this.resizeObserver && this.$refs.signatureCanvas) {
          this.resizeObserver.observe(this.$refs.signatureCanvas.parentElement);
        } else {
          // Fallback to old method if ResizeObserver not supported
          window.addEventListener('resize', this.handleWindowResize, { passive: true });
        }

        window.addEventListener('orientationchange', this.handleOrientationChange, { passive: true });
      });
    },

    closeSignaturePanel() {
      // Restore body scrolling
      document.body.style.overflow = '';

      // Clean up event listeners
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.handleWindowResize);
      }

      window.removeEventListener('orientationchange', this.handleOrientationChange);

      // Clear rotation lock detection timer
      if (this.orientationCheckTimer) {
        clearTimeout(this.orientationCheckTimer);
      }

      this.showSignaturePanel = false;

      // Destroy signature pad
      if (this.signaturePad) {
        this.signaturePad.off();
        this.signaturePad = null;
      }
    },

    checkDeviceAndOrientation() {
      this.isPortraitMode = window.innerHeight > window.innerWidth;
      this.isTablet = window.innerWidth >= this.tabletMinWidth;
    },

    // Check if device rotation is locked - optimized
    checkRotationLock() {
      // Reset counter
      this.orientationChangeCount = 0;
      this.rotationLockDetected = false;

      // Clear any existing timer
      if (this.orientationCheckTimer) {
        clearTimeout(this.orientationCheckTimer);
      }

      // Rotation lock warning remains informational only
      if (this.isPortraitMode && !this.isTablet) {
        const initialOrientation = window.innerHeight > window.innerWidth;

        // Check after 3 seconds - if no orientation changes detected despite resize events
        this.orientationCheckTimer = setTimeout(() => {
          const currentOrientation = window.innerHeight > window.innerWidth;
          this.rotationLockDetected = initialOrientation === currentOrientation && this.orientationChangeCount === 0;
        }, 3000);
      }
    },

    normalizeSignatureData(strokes, sourceWidth, sourceHeight, targetWidth, targetHeight) {
      const safeSourceWidth = Math.max(sourceWidth || 0, 1);
      const safeSourceHeight = Math.max(sourceHeight || 0, 1);
      const scaleX = targetWidth / safeSourceWidth;
      const scaleY = targetHeight / safeSourceHeight;

      return strokes.map((stroke) => ({
        ...stroke,
        points: stroke.points.map((point) => ({
          ...point,
          x: point.x * scaleX,
          y: point.y * scaleY
        }))
      }));
    },

    generateSignatureImage() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        return null;
      }

      const sourceCanvas = this.$refs.signatureCanvas;
      if (!sourceCanvas) {
        return this.signaturePad.toDataURL('image/png', 0.8);
      }

      // Keep output dimensions consistent across portrait/landscape signing.
      const targetWidth = 1000;
      const targetHeight = 360;

      const sourceRect = sourceCanvas.getBoundingClientRect();
      const sourceWidth = sourceRect.width || sourceCanvas.width / this.dpr || targetWidth;
      const sourceHeight = sourceRect.height || sourceCanvas.height / this.dpr || targetHeight;

      const normalizedData = this.normalizeSignatureData(
          this.signaturePad.toData(),
          sourceWidth,
          sourceHeight,
          targetWidth,
          targetHeight
      );

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = targetWidth;
      exportCanvas.height = targetHeight;

      const exportSignaturePad = new SignaturePad(exportCanvas, {
        minWidth: 1.0,
        maxWidth: 2.5,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        penColor: 'black',
        throttle: 16,
        velocityFilterWeight: 0.7
      });

      exportSignaturePad.clear();
      exportSignaturePad.fromData(normalizedData);

      return exportCanvas.toDataURL('image/png', 0.8);
    },

    initSignaturePad() {
      if (!this.$refs.signatureCanvas) return;

      // Get container dimensions
      const container = this.$refs.signatureCanvas.parentElement;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight || 200;

      // Clean up existing signature pad
      if (this.signaturePad) {
        this.signatureData = this.signaturePad.isEmpty() ? null : this.signaturePad.toData();
        this.signaturePad.off();
      }

      const canvas = this.$refs.signatureCanvas;

      // Set canvas dimensions with device pixel ratio for sharp rendering
      // Use cached DPR value for performance
      canvas.width = containerWidth * this.dpr;
      canvas.height = containerHeight * this.dpr;

      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      const ctx = canvas.getContext('2d', { alpha: true });

      // Clear transformations and apply DPR scaling
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(this.dpr, this.dpr);

      // Clear canvas - more efficient than clearRect for the entire canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create signature pad with optimized settings
      this.signaturePad = new SignaturePad(canvas, {
        minWidth: 1.0,
        maxWidth: 2.5,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        penColor: 'black',
        throttle: 16, // Matching 60fps
        velocityFilterWeight: 0.7
      });

      // Optimized event listener - only track "endStroke" events
      this.signaturePad.addEventListener("endStroke", () => {
        const isEmpty = this.signaturePad.isEmpty();
        // Only update if state changed
        if (this.isSignatureDrawn !== !isEmpty) {
          this.isSignatureDrawn = !isEmpty;
          this.signatureData = !isEmpty ? this.signaturePad.toData() : null;
        }
      });

      // Restore previous signature data if available
      if (this.signatureData && this.signatureData.length > 0) {
        this.signaturePad.fromData(this.signatureData);
        this.isSignatureDrawn = true;
      }
    },

    clearSignature() {
      if (this.signaturePad) {
        this.signaturePad.clear();
        this.isSignatureDrawn = false;
        this.signatureData = null;
      }
    },

    completeSignature() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        alert('請提供你的完整姓名簽名');
        return;
      }

      // Export a normalized PNG size for consistent backend processing.
      const signatureData = this.generateSignatureImage();
      if (!signatureData) {
        alert('簽名處理失敗，請重新簽署');
        return;
      }

      // Emit event with signature data
      this.$emit('signature-completed', signatureData);

      // Close panel
      this.closeSignaturePanel();
    },

    // Legacy resize handler (used as fallback if ResizeObserver not supported)
    handleWindowResize: function() {
      // Already debounced in the created function
      this.checkDeviceAndOrientation();

      if (this.showSignaturePanel) {
        requestAnimationFrame(() => this.initSignaturePad());
      }
    },

    handleOrientationChange() {
      // Track orientation changes for rotation lock detection
      this.orientationChangeCount++;

      // If we've detected orientation changes, device likely doesn't have rotation lock
      if (this.orientationChangeCount > 0) {
        this.rotationLockDetected = false;
      }

      // Reset and check orientation after device rotation - use RAF for performance
      requestAnimationFrame(() => {
        this.checkDeviceAndOrientation();

        // Keep signature pad responsive in both portrait and landscape.
        if (this.showSignaturePanel) {
          requestAnimationFrame(() => {
            this.initSignaturePad();
          });
        }
      });
    }
  },
  beforeUnmount() {
    // Clean up all resources
    if (this.orientationCheckTimer) {
      clearTimeout(this.orientationCheckTimer);
    }

    if (this.signaturePad && typeof this.signaturePad.off === 'function') {
      this.signaturePad.off();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.handleWindowResize);
    }

    window.removeEventListener('orientationchange', this.handleOrientationChange);

    // Ensure body scrolling is restored
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
/* Signature prompt section - performance optimized styles */
.signature-prompt {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  will-change: transform; /* Optimize for animations */
  contain: content; /* Improve rendering performance */
}

.signature-guide {
  display: flex;
  align-items: center;
  gap: 12px;
}

.guide-icon {
  color: #FF9E15;
  background-color: #FFF5E6;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.guide-text h3 {
  color: #333333;
  font-weight: 600;
  font-size: 15px;
  margin: 0;
}

.guide-text p {
  margin: 4px 0 0;
  color: #666666;
  font-size: 14px;
}

.guide-text strong {
  color: #FF9E15;
  font-weight: 600;
}

.sign-btn {
  background-color: #FF9E15;
  color: #333333;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  width: 100%;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  contain: content; /* Improve rendering performance */
}

.sign-btn:active {
  background-color: #E68A00;
}

/* Signature panel section - optimized with GPU acceleration */
.signature-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 0;
  will-change: opacity;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  contain: strict; /* Strict containment for overlay */
}

/* Signature panel wrapper - optimized */
.signature-panel-wrapper {
  width: 90%;
  height: auto;
  max-width: 680px;
  max-height: 90vh;
  aspect-ratio: 1.6 / 1; /* Landscape-ish ratio */
  transform: translateZ(0); /* Force GPU acceleration */
  contain: layout style; /* Improve rendering performance */
}

.signature-panel {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  contain: content; /* Improve rendering performance */
}

.signature-panel-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #dadce0;
  flex-shrink: 0;
}

.close-btn {
  background: transparent;
  border: none;
  color: #666666;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.close-btn:active {
  background-color: #f8f9fa;
}

.signature-container {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0; /* Allow container to shrink */
  border-bottom: 1px solid #dadce0;
  overflow: hidden;
  contain: size layout; /* Improve rendering performance */
}

.signature-pad {
  width: 100%;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
  position: absolute;
  top: 0;
  left: 0;
  -webkit-touch-callout: none; /* Prevent iOS callout */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version */
  will-change: transform; /* Optimize for drawing */
}

.signature-instructions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.7);
}

.instruction-icon {
  color: #FF9E15;
  opacity: 0.8;
  margin-bottom: 8px;
}

.signature-instructions p {
  margin: 0;
  color: #666666;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  padding: 0 16px;
}

.signature-panel-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  flex-shrink: 0;
  flex-direction: row;
}

.secondary-btn, .primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  min-width: 100px;
  font-size: 14px;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.secondary-btn {
  background-color: white;
  color: #666666;
  border: 1px solid #dadce0;
}

.secondary-btn:active {
  background-color: #f8f9fa;
}

.primary-btn {
  background-color: #FF9E15;
  color: #333333;
  border: none;
}

.primary-btn:active:not(:disabled) {
  background-color: #E68A00;
}

.primary-btn:disabled {
  background-color: #FFD699;
  cursor: not-allowed;
}

.legal-disclaimer {
  padding: 0 12px 10px;
  font-size: 11px;
  color: #666666;
  text-align: center;
  margin: 0;
  flex-shrink: 0;
}

.button-icon {
  flex-shrink: 0;
}

/* Tablet-specific styles */
@media (min-width: 768px) {
  .signature-panel-wrapper {
    width: 85%;
    max-width: 800px;
    aspect-ratio: auto; /* Allow more flexible ratio on tablets */
    height: 60vh;
    max-height: 500px;
  }
}

/* Mobile-specific styles - optimized */
@media (max-width: 767px) {
  .signature-prompt {
    padding: 12px;
  }

  .guide-icon {
    width: 32px;
    height: 32px;
  }

  .signature-panel-wrapper {
    width: 94%;
    max-width: 640px;
    height: 72vh;
    max-height: 520px;
    aspect-ratio: auto;
  }

  /* Hide button icons on very small screens */
  @media (max-width: 360px) {
    .button-icon {
      display: none;
    }

    .secondary-btn, .primary-btn {
      min-width: auto;
      padding: 10px 12px;
    }
  }
}

/* Extra small screens - optimized */
@media (max-height: 400px) and (orientation: landscape) {
  .signature-panel-wrapper {
    height: 80vh;
  }

  .signature-panel-actions {
    padding: 8px;
  }

  .legal-disclaimer {
    padding: 0 8px 8px;
    font-size: 10px;
  }
}
</style>